import React from "react";
import { Box, Grid, Typography } from "@mui/material";

/**
 * The light panel the Hi-Fi frames draw around each grouped section — "Treatment service
 * assignment" and "First appointment" on 8a, "New first appointment" on 8b.
 *
 * The sections were previously bare headings on the modal background, so they read as a
 * continuous list of fields rather than the two distinct, separately-gated blocks the design
 * shows. The panel is what makes "both sections must be complete" legible as a statement about
 * two things.
 *
 * Title takes the brand royal (#1A43D2 via palette.primary) rather than body ink, per the frames.
 */

interface Props {
  title: string;
  /** Sub-line under the title, e.g. "The participant will attend here." */
  subtitle?: string;
  /** Marks the title with the required asterisk the frames show on "First appointment *". */
  required?: boolean;
  children: React.ReactNode;
}

const SectionPanel: React.FC<Props> = ({
  title,
  subtitle,
  required,
  children,
}) => (
  <Grid item xs={12}>
    <Box
      sx={{
        // Theme tokens, not literals: palette.panel carries the Hi-Fi fill (#F5F8FF) and border
        // (#C7D6F5). Previously action.hover / divider, which are neutral greys — the sections
        // read as disabled rather than grouped.
        backgroundColor: "panel.main",
        border: "1px solid",
        borderColor: "panel.dark",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: "primary.main" }}
      >
        {title}
        {required ? " *" : ""}
      </Typography>

      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      ) : null}

      <Grid container spacing={2} sx={{ mt: 0 }}>
        {children}
      </Grid>
    </Box>
  </Grid>
);

export default SectionPanel;
